// TennisTrack Hub — Dettaglio Mio Template (Locale) | 029 Project
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import Navbar from '../../components/Navbar';
import './Template.css';

const DettaglioMioTemplate = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { utente } = useAuth();

  const [template, setTemplate] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [errore, setErrore] = useState('');
  const [invioLoading, setInvioLoading] = useState(false);
  const [invioMsg, setInvioMsg] = useState('');

  const carica = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get(`/template/miei/${id}`);
      setTemplate(res.data.template);
    } catch (err) {
      setErrore(err.response?.status === 404
        ? 'Template non trovato.'
        : 'Errore nel caricamento.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { carica(); }, [carica]);

  const inviaRevisione = async () => {
    if (!window.confirm('Inviare questo template per revisione? Verrà esaminato dal team 029 Project.')) return;
    setInvioLoading(true);
    setErrore('');
    try {
      const res = await api.post(`/template/miei/${id}/invia`);
      setInvioMsg(res.data.message);
      await carica();
    } catch (err) {
      setErrore(err.response?.data?.error || 'Errore nell\'invio.');
    } finally {
      setInvioLoading(false);
    }
  };

  const scaricaPdf = async () => {
    if (utente?.piano !== 'premium') { navigate('/premium'); return; }
    setPdfLoading(true);
    try {
      const res = await api.get(`/pdf/template/${id}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `template_${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      alert('Errore nella generazione del PDF.');
    } finally {
      setPdfLoading(false);
    }
  };

  const formatData = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  if (loading) return (
    <div className="page-layout">
      <Navbar />
      <main className="page-main"><div className="loading-msg">Caricamento...</div></main>
    </div>
  );

  if (errore && !template) return (
    <div className="page-layout">
      <Navbar />
      <main className="page-main">
        <div className="alert alert-error">{errore}</div>
        <button className="btn btn-secondary" onClick={() => navigate('/template')}>← Torna ai template</button>
      </main>
    </div>
  );

  const contenuto = typeof template?.contenuto === 'string'
    ? JSON.parse(template.contenuto || '{}')
    : (template?.contenuto || {});

  const esercizi = contenuto?.esercizi || [];

  return (
    <div className="page-layout">
      <Navbar />
      <main className="page-main">

        {/* Header */}
        <div className="page-header">
          <div>
            <h1>
              {template.tipo === 'tennis' ? '🎾' : '💪'} {template.titolo}
            </h1>
            <p>
              {template.is_fork && <span className="tc-fork-badge" style={{ marginRight: '8px' }}>fork</span>}
              {template.inviato_revisione && <span className="tc-revisione-badge" style={{ marginRight: '8px' }}>📤 in revisione</span>}
              <span style={{ color: 'var(--text-secondary)', fontSize: '0.88rem' }}>
                {template.livello} · modificato {formatData(template.updated_at)}
              </span>
            </p>
          </div>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button
              className="btn btn-secondary"
              onClick={scaricaPdf}
              disabled={pdfLoading}
              title={utente?.piano !== 'premium' ? 'Funzione Premium' : 'Scarica PDF'}
            >
              {pdfLoading ? '⏳...' : utente?.piano === 'premium' ? '📄 PDF' : '🔒 PDF'}
            </button>
            <Link to={`/template/miei/${id}/modifica`} className="btn btn-secondary">
              ✏️ Modifica
            </Link>
            <button className="btn btn-secondary" onClick={() => navigate('/template')}>
              ← Indietro
            </button>
          </div>
        </div>

        {errore && <div className="alert alert-error">{errore}</div>}
        {invioMsg && <div className="alert alert-success">✅ {invioMsg}</div>}

        <div className="template-detail">

          {/* Info */}
          {template.descrizione && (
            <div className="card" style={{ marginBottom: '16px' }}>
              <p className="td-desc">{template.descrizione}</p>
            </div>
          )}

          {/* Esercizi */}
          {esercizi.length > 0 ? (
            <div className="card" style={{ marginBottom: '20px' }}>
              <h3 style={{ marginBottom: '16px', fontSize: '1rem' }}>
                {template.tipo === 'tennis' ? '🎾' : '💪'} Esercizi ({esercizi.length})
              </h3>
              <div className="esercizi-dettaglio">
                {esercizi.map((es, idx) => (
                  <div key={idx} className="es-card">
                    <div className="es-card-header">
                      <span className="es-num">#{idx + 1}</span>
                      {template.tipo === 'tennis' && es.colpo && (
                        <strong style={{ color: 'var(--text-primary)' }}>{es.colpo}</strong>
                      )}
                      {template.tipo === 'fitness' && es.nome && (
                        <strong style={{ color: 'var(--text-primary)' }}>{es.nome}</strong>
                      )}
                      {template.tipo === 'fitness' && es.tipologia && (
                        <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>— {es.tipologia}</span>
                      )}
                    </div>

                    {template.tipo === 'tennis' ? (
                      <div className="es-card-body">
                        {es.difficolta && (
                          <div className="es-field" style={{ gridColumn: '1 / -1' }}>
                            <span className="es-label">Difficoltà</span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <div className="difficolta-bar" style={{ flex: 1 }}>
                                <div
                                  className="difficolta-fill"
                                  style={{ width: `${(es.difficolta / 10) * 100}%` }}
                                />
                              </div>
                              <span className="es-value">{es.difficolta}/10</span>
                            </div>
                          </div>
                        )}
                        {es.serie && (
                          <div className="es-field">
                            <span className="es-label">Serie</span>
                            <span className="es-value">{es.serie}</span>
                          </div>
                        )}
                        {es.ripetizioni && (
                          <div className="es-field">
                            <span className="es-label">Ripetizioni</span>
                            <span className="es-value">{es.ripetizioni}</span>
                          </div>
                        )}
                        {es.durata_min && (
                          <div className="es-field">
                            <span className="es-label">Durata</span>
                            <span className="es-value">{es.durata_min} min</span>
                          </div>
                        )}
                        {es.descrizione && (
                          <div className="es-field" style={{ gridColumn: '1 / -1' }}>
                            <span className="es-label">Descrizione</span>
                            <span className="es-value">{es.descrizione}</span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="es-card-body">
                        {es.serie && (
                          <div className="es-field">
                            <span className="es-label">Serie</span>
                            <span className="es-value">{es.serie}</span>
                          </div>
                        )}
                        {es.ripetizioni && (
                          <div className="es-field">
                            <span className="es-label">Ripetizioni</span>
                            <span className="es-value">{es.ripetizioni}</span>
                          </div>
                        )}
                        {es.carico_kg && (
                          <div className="es-field">
                            <span className="es-label">Carico</span>
                            <span className="es-value">{es.carico_kg} kg</span>
                          </div>
                        )}
                        {es.note && (
                          <div className="es-field" style={{ gridColumn: '1 / -1' }}>
                            <span className="es-label">Note</span>
                            <span className="es-value">{es.note}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="card" style={{ marginBottom: '20px' }}>
              <div className="empty-state" style={{ padding: '24px' }}>
                <p>Nessun esercizio ancora. <Link to={`/template/miei/${id}/modifica`} style={{ color: 'var(--color-primary)' }}>Modifica il template</Link> per aggiungerne.</p>
              </div>
            </div>
          )}

          {/* Pubblica su libreria — solo premium */}
          {!template.inviato_revisione && (
            <div className="card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' }}>
              <div>
                <strong style={{ color: 'var(--text-primary)' }}>📤 Pubblica sulla libreria</strong>
                <p style={{ margin: '4px 0 0', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                  Condividi questo template con la community (richiede piano Premium).
                </p>
              </div>
              <button
                className="btn btn-secondary"
                onClick={inviaRevisione}
                disabled={invioLoading}
              >
                {invioLoading ? 'Invio...' : '📤 Invia per revisione'}
              </button>
            </div>
          )}

          {template.inviato_revisione && (
            <div className="card">
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                <span>📤</span>
                <span>Template inviato per revisione. Ti notificheremo l'esito.</span>
              </div>
            </div>
          )}

        </div>
      </main>
    </div>
  );
};

export default DettaglioMioTemplate;
